import { CheckCircle, Crown, Star, Zap, Rocket, Sparkles, Award } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";

// Badge definitions - rozet tanımları
export const BADGE_CONFIG = {
  verified: {
    name_tr: "Onaylı Kullanıcı",
    name_en: "Verified User",
    color: "emerald",
    Icon: CheckCircle,
    bgClass: "bg-emerald-500/20",
    textClass: "text-emerald-400",
    borderClass: "border-emerald-500/50"
  },
  vip: {
    name_tr: "VIP Üye",
    name_en: "VIP Member",
    color: "purple",
    Icon: Crown,
    bgClass: "bg-purple-500/20",
    textClass: "text-purple-400",
    borderClass: "border-purple-500/50"
  },
  gold: {
    name_tr: "Altın Üye",
    name_en: "Gold Member",
    color: "yellow",
    Icon: Star,
    bgClass: "bg-yellow-500/20",
    textClass: "text-yellow-400",
    borderClass: "border-yellow-500/50"
  },
  premium: {
    name_tr: "Premium",
    name_en: "Premium",
    color: "blue",
    Icon: Zap,
    bgClass: "bg-blue-500/20",
    textClass: "text-blue-400",
    borderClass: "border-blue-500/50"
  },
  pioneer: {
    name_tr: "Öncü",
    name_en: "Pioneer",
    color: "orange",
    Icon: Rocket,
    bgClass: "bg-orange-500/20",
    textClass: "text-orange-400",
    borderClass: "border-orange-500/50"
  },
  star: {
    name_tr: "Yıldız Müşteri",
    name_en: "Star Customer",
    color: "pink",
    Icon: Sparkles,
    bgClass: "bg-pink-500/20",
    textClass: "text-pink-400",
    borderClass: "border-pink-500/50"
  }
};

// Single Badge Component
export const UserBadge = ({ badge, language, size = "default" }) => {
  const config = BADGE_CONFIG[badge];
  if (!config) return null;
  
  const { Icon, bgClass, textClass, borderClass, name_tr, name_en } = config;
  const badgeName = language === "tr" ? name_tr : name_en;
  
  const sizeClasses = size === "small" 
    ? "px-2 py-0.5 text-[10px] gap-1" 
    : "px-2.5 py-1 text-xs gap-1.5";
  const iconSize = size === "small" ? "w-3 h-3" : "w-3.5 h-3.5";
  
  return (
    <span 
      className={`inline-flex items-center ${sizeClasses} ${bgClass} ${textClass} border ${borderClass} rounded-full font-mono font-medium whitespace-nowrap`}
      title={badgeName}
    >
      <Icon className={iconSize} />
      <span>{badgeName}</span>
    </span>
  );
};

// Badge List Component - Shows ALL user badges (no limit)
export const UserBadgeList = ({ user, language, size = "small" }) => {
  if (!user?.badges?.length) return null;
  
  return (
    <div className="flex flex-wrap gap-1">
      {user.badges.map((badge, idx) => (
        <UserBadge key={idx} badge={badge} language={language} size={size} />
      ))}
    </div>
  );
};

// User Info with Badges - For sidebar display
export const UserInfoWithBadges = ({ user, language }) => {
  if (!user) return null;
  
  return (
    <div className="mb-4">
      <p className="font-mono text-sm text-muted-foreground">
        {language === "tr" ? "Hoşgeldin," : "Welcome,"}
      </p>
      <p className="font-mono font-bold truncate">{user?.name}</p>
      
      {/* User Badges - Show ALL badges */}
      {user?.badges?.length > 0 && (
        <div className="mt-2">
          <UserBadgeList user={user} language={language} size="small" />
        </div>
      )}
    </div>
  );
};

// Badge Notification Modal
export const BadgeNotificationModal = ({ badges, language, onClose }) => {
  if (!badges || badges.length === 0) return null;
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="glass border-primary/30 max-w-md">
        <div className="text-center py-6">
          <div className="w-20 h-20 mx-auto mb-6 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-yellow-500/30 rounded-full animate-pulse"></div>
            <div className="relative w-full h-full flex items-center justify-center">
              <Award className="w-12 h-12 text-primary" />
            </div>
          </div>
          
          <h2 className="text-2xl font-mono font-bold text-primary mb-2">
            {language === "tr" ? "🎉 Tebrikler!" : "🎉 Congratulations!"}
          </h2>
          
          <p className="text-muted-foreground mb-6">
            {language === "tr" 
              ? "Admin tarafından ödüllendirildiniz!"
              : "You have been rewarded by admin!"}
          </p>
          
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {badges.map((badge, idx) => (
              <UserBadge key={idx} badge={badge} language={language} size="default" />
            ))}
          </div>
          
          <Button 
            onClick={onClose} 
            className="font-mono bg-primary text-black hover:bg-primary/90"
          >
            {language === "tr" ? "Harika!" : "Awesome!"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default { UserBadge, UserBadgeList, UserInfoWithBadges, BadgeNotificationModal, BADGE_CONFIG };
